import { useState } from 'react';
import { ChevronDown, ChevronUp, X, BookOpen, Shield, Clock, Smartphone, Phone, AlertTriangle, HandshakeIcon } from 'lucide-react';

const RED = '#b83228';

// ── Contenu du document ────────────────────────────────────────────────────

const SECTIONS = [
  {
    id: 'mission',
    num: '1',
    title: 'Notre Mission Commune',
    icon: BookOpen,
    roles: ['admin', 'driver', 'gestionnaire'],
    content: [
      {
        type: 'text',
        text: "Wakaana Express est une entreprise de livraison. Notre activité repose sur un principe simple : une commande est passée, nous la livrons dans les meilleures conditions. C'est sur cette base que l'entreprise fonctionne, que les clients nous font confiance et que chacun d'entre vous a un emploi stable.",
      },
      {
        type: 'text',
        text: "Chaque livraison réussie est un client satisfait qui recommande notre service, qui commande à nouveau et qui contribue à notre réputation. À l'inverse, chaque livraison ratée, chaque retard injustifié ou chaque négligence nous coûte — en argent, en crédibilité et en opportunités perdues.",
      },
      {
        type: 'highlight',
        color: RED,
        text: "Un livreur qui ne livre pas → un client mécontent → une boutique partenaire qui perd confiance → moins de commandes pour tout le monde. La chaîne est directe. La responsabilité est partagée.",
      },
    ],
  },
  {
    id: 'roles',
    num: '2',
    title: 'Rôles et Responsabilités',
    icon: Shield,
    roles: ['admin', 'driver', 'gestionnaire'],
    subsections: [
      {
        role: 'driver',
        title: 'LE LIVREUR',
        intro: "Le livreur est le visage de Wakaana Express sur le terrain. C'est lui que le client voit, c'est lui qui représente notre sérieux et notre professionnalisme.",
        items: [
          "Prendre en charge les commandes assignées et les livrer dans les meilleurs délais",
          "Traiter chaque client avec respect et courtoisie",
          "Signaler immédiatement tout problème rencontré lors d'une livraison",
          "Prendre soin du matériel et des colis qui lui sont confiés",
          "Utiliser l'application correctement et en temps réel",
        ],
        note: "Être livreur chez Wakaana Express, ce n'est pas un poste où l'on est payé simplement pour être présent. La rémunération est liée à la performance collective. Un livreur qui ne s'implique pas pèse sur toute l'équipe.",
      },
      {
        role: 'gestionnaire',
        title: 'LE GESTIONNAIRE',
        intro: "Le gestionnaire est le cerveau opérationnel de Wakaana Express. Sa réactivité et son sérieux déterminent directement notre taux de livraison réussie. Un gestionnaire lent, c'est un client perdu.",
        items: [
          "Être à l'heure à chaque prise de service, sans exception",
          "Appeler le client immédiatement à la réception de chaque nouvelle commande — pas dans 10 minutes, pas après le café : sur le champ",
          "Rappeler systématiquement tout client qui n'a pas répondu au premier appel",
          "Rappeler tout client ayant demandé à être recontacté, quel que soit le motif (rappel, indisponibilité, report de livraison, etc.)",
          "Ne jamais laisser une commande sans tentative de contact client le même jour",
          "Assigner les commandes aux livreurs de manière efficace et équitable",
          "Suivre en temps réel l'avancement des livraisons via l'application",
          "Être disponible et réactif pendant toute la durée de son service",
          "Remonter les problèmes au responsable sans délai",
          "Maintenir une communication claire et professionnelle avec les livreurs et les boutiques partenaires",
        ],
        note: "La réactivité, ce n'est pas une qualité optionnelle. Dans notre secteur, un client peut passer commande sur deux plateformes en même temps. Seul celui qui rappelle en premier livrera. Chaque minute perdue est une commande que le concurrent prend à notre place. La paresse au téléphone coûte de l'argent à toute l'équipe.",
      },
    ],
  },
  {
    id: 'assiduite',
    num: '3',
    title: 'Assiduité et Ponctualité',
    icon: Clock,
    roles: ['admin', 'driver', 'gestionnaire'],
    content: [
      {
        type: 'text',
        text: "La ponctualité n'est pas une option, c'est une exigence professionnelle de base. Wakaana Express fonctionne selon des horaires précis. Les clients, les boutiques partenaires et vos collègues comptent sur vous à l'heure convenue.",
      },
      {
        type: 'subtitle',
        text: 'HORAIRES DE TRAVAIL',
      },
      {
        type: 'list',
        items: [
          "Prise de service : 10h00",
          "Fin de service : 19h00",
          "Durée journalière : 9 heures de travail effectif",
          "Une tolérance de 30 minutes est accordée — toute arrivée après 10h30 est considérée comme un retard officiel",
        ],
      },
      {
        type: 'subtitle',
        text: 'LES RETARDS',
      },
      {
        type: 'text',
        text: "Tout retard non justifié est enregistré automatiquement par l'application et fait l'objet d'une déduction sur le salaire du mois concerné.",
      },
      {
        type: 'list',
        items: [
          "Tout retard doit être signalé avant l'heure de prise de service, pas après",
          "Un retard signalé à temps avec une raison valable sera examiné et pourra être excusé",
          "Un retard non signalé sera automatiquement considéré comme injustifié",
          "Les retards répétés, même justifiés, feront l'objet d'un entretien avec le responsable",
        ],
      },
      {
        type: 'sublist',
        title: 'Calcul de la déduction retard :',
        items: [
          "La déduction est calculée au pro-rata exact à la minute de retard",
          "Formule : salaire mensuel ÷ jours ouvrés du mois (jours de repos inclus, car chômés payés) ÷ 540 × minutes de retard",
          "Ce calcul est effectué automatiquement par l'application chaque mois",
          "Le résultat est visible dans la section Présences du tableau de bord",
        ],
      },
      {
        type: 'subtitle',
        text: 'LES ABSENCES',
      },
      {
        type: 'list',
        items: [
          "Toute absence doit être communiquée au moins la veille, sauf urgence réelle",
          "Une absence le jour même doit être signalée avant 10h00",
          "Toute absence non signalée sera automatiquement traitée comme injustifiée",
        ],
      },
      {
        type: 'sublist',
        title: 'Absence justifiée (maladie avec certificat, urgence familiale grave) :',
        items: [
          "La journée peut être excusée sur décision du responsable",
          "Un justificatif est obligatoire — sans preuve, l'absence sera traitée comme non justifiée",
          "Doit être signalée le plus tôt possible, même en cas d'urgence",
        ],
      },
      {
        type: 'sublist',
        title: 'Absence non justifiée :',
        items: [
          "Journée complète déduite du salaire (salaire mensuel ÷ jours ouvrés du mois)",
          "Consignée automatiquement dans le dossier de l'employé via l'application",
          "3 absences injustifiées dans le mois = convocation et avertissement formel",
          "Au-delà : licenciement possible sans préavis",
        ],
      },
      {
        type: 'subtitle',
        text: 'LE JOUR DE REPOS',
      },
      {
        type: 'text',
        text: "Chaque membre de l'équipe a droit à un jour de repos par semaine. Ce jour est chômé et payé — il est compris dans le salaire mensuel et ne donne lieu à aucune déduction.",
      },
      {
        type: 'list',
        items: [
          "Le jour de repos est attribué par roulement selon les besoins opérationnels",
          "C'est l'application qui programme et notifie le jour de repos de chacun",
          "Le jour de repos ne peut pas être choisi unilatéralement par l'employé",
          "Travailler le jour de repos attribué n'est possible que sur demande expresse du responsable",
          "Ne pas se présenter un jour de travail ordinaire sous prétexte du repos = absence injustifiée",
        ],
      },
      {
        type: 'highlight',
        color: RED,
        text: "Ce qui ne sera plus toléré : arriver en retard sans prévenir · ne pas se présenter sans donner de nouvelles · prévenir à la dernière minute de manière répétée sans raison valable.",
      },
    ],
  },
  {
    id: 'application',
    num: '4',
    title: "L'Application : Règles d'Utilisation",
    icon: Smartphone,
    roles: ['admin', 'driver', 'gestionnaire'],
    content: [
      {
        type: 'subtitle',
        text: 'DÉMARRER LA JOURNÉE',
      },
      {
        type: 'list',
        items: [
          "Activation obligatoire dès la prise de service, au moment précis où vous commencez à travailler",
          "Ne doit être désactivée qu'à la fin effective du service",
          "Ne pas l'activer = absence ou retard injustifié",
          "La désactiver en cours de journée sans raison valable est interdit",
        ],
      },
      {
        type: 'note',
        text: "Cette fonctionnalité permet de coordonner les assignations et d'assurer un suivi fiable. Ce n'est pas un outil de surveillance, c'est un outil de coordination au service de toute l'équipe.",
      },
      {
        type: 'subtitle',
        text: 'RÈGLES GÉNÉRALES',
      },
      {
        type: 'list',
        items: [
          "L'application doit être ouverte et active pendant toute la durée du service",
          "Toute mise à jour de statut doit être faite en temps réel, pas en fin de journée",
          "Les informations saisies doivent être exactes et honnêtes",
        ],
      },
      {
        type: 'subtitle',
        text: 'FRAUDE ET MANIPULATION DES DONNÉES — TOLÉRANCE ZÉRO',
      },
      {
        type: 'text',
        text: "L'application enregistre chaque action effectuée. Tout est tracé, tout est visible.",
        bold: true,
      },
      {
        type: 'sublist',
        title: 'Actes considérés comme fautes graves :',
        items: [
          "Modifier le montant d'une commande sans autorisation",
          "Marquer une commande comme livrée sans l'avoir réellement livrée",
          "Falsifier un statut de livraison",
          "Toute tentative de manipulation des données à des fins personnelles",
        ],
      },
      {
        type: 'sublist',
        title: 'Sanctions applicables :',
        items: [
          "Déduction immédiate du montant fraudé sur le salaire",
          "Convocation devant le responsable",
          "Licenciement immédiat sans préavis en cas de fraude grave ou répétée",
          "Poursuite judiciaire si le préjudice causé le justifie",
        ],
      },
      {
        type: 'highlight',
        color: RED,
        text: "La confiance est le fondement de notre équipe. Celui qui la brise met en danger non seulement sa place, mais l'ensemble de l'entreprise.",
      },
    ],
  },
  {
    id: 'telephone',
    num: '5',
    title: 'Lignes Téléphoniques et Connexion',
    icon: Phone,
    roles: ['admin', 'driver', 'gestionnaire'],
    content: [
      {
        type: 'text',
        text: "Toute l'équipe travaille sur le réseau Tigo. Le téléphone et la connexion internet sont des outils de travail, au même titre qu'un vélo ou un casque. Sans crédit et sans connexion, il n'y a pas de travail possible.",
      },
      {
        type: 'subtitle',
        text: 'FORFAITS PRIS EN CHARGE PAR L\'ENTREPRISE',
      },
      {
        type: 'sublist',
        title: 'Forfait mensuel — 3 000 FCFA (accordé à tous en début de mois)',
        items: [
          "300 minutes d'appel",
          "100 SMS",
          "24 Go de données internet",
          "Ce forfait couvre les appels clients, la navigation sur l'application et le travail quotidien",
        ],
      },
      {
        type: 'sublist',
        title: 'Forfait de recharge en cours de mois — 2 000 FCFA',
        items: [
          "750 minutes d'appel",
          "Accordé une fois si les minutes du forfait de 3 000 FCFA sont épuisées en cours de mois",
          "Ce forfait est spécifiquement prévu pour les gestionnaires qui passent beaucoup d'appels",
        ],
      },
      {
        type: 'subtitle',
        text: 'AU-DELÀ DE CES FORFAITS',
      },
      {
        type: 'list',
        items: [
          "Tout renouvellement supplémentaire est à la charge personnelle du livreur ou du gestionnaire",
          "Le rachat est obligatoire — il n'existe aucune excuse valable liée à l'absence de crédit ou de connexion",
          "Anticipe : prévois ce coût sur ton salaire, ne laisse pas ta ligne s'épuiser avant d'agir",
          "Ne pas recharger sa ligne = ne pas pouvoir travailler = absence injustifiée",
        ],
      },
      {
        type: 'subtitle',
        text: 'PRÉSENCE SUR LA CARTE — OBLIGATION ABSOLUE POUR LES LIVREURS',
      },
      {
        type: 'text',
        text: "L'application permet de localiser les livreurs en temps réel sur la carte. Cette fonctionnalité est essentielle pour coordonner les livraisons, optimiser les assignations et donner de la visibilité à toute l'équipe.",
      },
      {
        type: 'list',
        items: [
          "Le livreur doit activer sa journée dès le début de son service — sans exception",
          "Tant que tu es en service, tu dois être visible sur la carte",
          "Désactiver sa présence en cours de journée sans raison valable est une faute",
          "Un livreur invisible sur la carte sera considéré comme absent ou en retard",
        ],
      },
      {
        type: 'highlight',
        color: RED,
        text: "Ce qui ne sera plus accepté : invoquer l'absence de connexion pour ne pas travailler · être injoignable pendant les heures de service · ne pas être visible sur la carte sans raison · attendre que l'entreprise règle systématiquement le problème.",
      },
    ],
  },
  {
    id: 'tolerance',
    num: '6',
    title: "Ce qu'on ne Tolère Plus",
    icon: AlertTriangle,
    roles: ['admin', 'driver', 'gestionnaire'],
    content: [
      {
        type: 'sublist',
        title: 'SUR LE TERRAIN',
        items: [
          "Refuser ou ignorer une commande assignée sans raison valable",
          "Abandonner une livraison en cours sans en informer le gestionnaire",
          "Maltraiter, manquer de respect ou intimider un client",
          "Se présenter en service dans un état inadapté au travail",
          "Utiliser les ressources de l'entreprise à des fins personnelles",
        ],
      },
      {
        type: 'sublist',
        title: "DANS L'ÉQUIPE",
        items: [
          "Les conflits entre collègues réglés sur le terrain au lieu d'être remontés au responsable",
          "Ne pas répondre aux appels ou messages pendant les heures de service",
          "Répandre de fausses informations ou créer des tensions au sein de l'équipe",
          "Manque de respect envers le gestionnaire ou le responsable",
        ],
      },
      {
        type: 'sublist',
        title: "SUR L'ASSIDUITÉ",
        items: [
          "Les retards répétés non justifiés",
          "Les absences sans préavis",
          "Quitter le service avant l'heure sans autorisation",
        ],
      },
      {
        type: 'sublist',
        title: 'SUR LES VERSEMENTS',
        items: [
          "Tout manque de versement constaté sera entièrement remboursé par le livreur concerné",
          "Le remboursement sera déduit directement sur le salaire du mois en cours",
          "Aucun argument ne sera accepté pour justifier un versement incomplet ou manquant",
          "En cas de récidive, des sanctions supplémentaires seront appliquées sans préavis",
        ],
      },
      {
        type: 'highlight',
        color: RED,
        text: "Tout comportement qui nuit au bon fonctionnement de l'équipe sera traité sérieusement. Les avertissements seront donnés. S'ils ne suffisent pas, des mesures plus fermes seront prises sans hésitation.",
      },
    ],
  },
  {
    id: 'engagements',
    num: '7',
    title: "Ce que l'Entreprise s'Engage à Faire",
    icon: HandshakeIcon,
    roles: ['admin', 'driver', 'gestionnaire'],
    content: [
      {
        type: 'sublist',
        title: 'RÉMUNÉRATION ET TRANSPARENCE',
        items: [
          "Chaque membre de l'équipe sera payé à temps, de manière correcte et transparente",
          "Les déductions éventuelles seront toujours expliquées et justifiées avant application",
          "Aucune déduction ne sera faite de manière arbitraire ou sans communication préalable",
        ],
      },
      {
        type: 'sublist',
        title: 'CONDITIONS DE TRAVAIL',
        items: [
          "L'entreprise s'engage à fournir les outils nécessaires au bon exercice de votre travail",
          "La première ligne téléphonique est prise en charge comme indiqué en section 5",
          "Tout problème lié aux outils de travail sera traité dans les meilleurs délais",
        ],
      },
      {
        type: 'sublist',
        title: 'ÉCOUTE ET RESPECT',
        items: [
          "Chaque membre a le droit d'exprimer ses préoccupations, ses idées ou ses difficultés",
          "Tout signalement sera traité avec sérieux et discrétion",
          "Le respect est exigé de tous, y compris de la direction envers l'équipe",
        ],
      },
      {
        type: 'sublist',
        title: 'ÉVOLUTION ET RECONNAISSANCE',
        items: [
          "Les membres sérieux, ponctuels et impliqués seront reconnus et valorisés",
          "Les performances individuelles seront prises en compte dans l'évolution au sein de l'entreprise",
          "Wakaana Express croit en la promotion interne — les opportunités viendront à ceux qui le méritent",
        ],
      },
      {
        type: 'highlight',
        color: '#173b78',
        text: "Nous construisons quelque chose ensemble. Les règles posées dans ce document ne sont pas là pour vous freiner, elles sont là pour nous permettre d'avancer dans le même sens, avec les mêmes repères. Une équipe solide, c'est une équipe qui dure.",
      },
    ],
  },
];

// ── Rendu d'un bloc de contenu ─────────────────────────────────────────────

function ContentBlock({ block }) {
  switch (block.type) {
    case 'text':
      return (
        <p style={{ fontSize: '13px', color: '#374151', lineHeight: 1.7, marginBottom: '10px', fontWeight: block.bold ? 700 : 400 }}>
          {block.text}
        </p>
      );
    case 'subtitle':
      return (
        <div style={{ fontSize: '11px', fontWeight: 800, color: '#1e3a6e', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', marginTop: '14px' }}>
          {block.text}
        </div>
      );
    case 'list':
      return (
        <ul style={{ paddingLeft: '16px', marginBottom: '10px' }}>
          {block.items.map((item, i) => (
            <li key={i} style={{ fontSize: '13px', color: '#374151', lineHeight: 1.7, marginBottom: '4px' }}>{item}</li>
          ))}
        </ul>
      );
    case 'sublist':
      return (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ fontSize: '11px', fontWeight: 800, color: '#1e3a6e', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: '6px', marginTop: '12px' }}>{block.title}</div>
          <ul style={{ paddingLeft: '16px' }}>
            {block.items.map((item, i) => (
              <li key={i} style={{ fontSize: '13px', color: '#374151', lineHeight: 1.7, marginBottom: '4px' }}>{item}</li>
            ))}
          </ul>
        </div>
      );
    case 'note':
      return (
        <div style={{ background: '#f0f3f8', borderRadius: '8px', borderLeft: '3px solid #1e3a6e', padding: '10px 13px', marginBottom: '10px', fontSize: '12px', color: '#374151', lineHeight: 1.6, fontStyle: 'italic' }}>
          {block.text}
        </div>
      );
    case 'highlight':
      return (
        <div style={{ background: block.color === RED ? '#fdf2f2' : '#f0f3f8', borderLeft: `4px solid ${block.color}`, borderRadius: '8px', padding: '12px 14px', marginBottom: '10px', fontSize: '13px', color: '#1e3a6e', lineHeight: 1.7, fontWeight: 600 }}>
          {block.text}
        </div>
      );
    default:
      return null;
  }
}

// ── Section avec accordéon ─────────────────────────────────────────────────

function Section({ section, role, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  const Icon = section.icon;

  const isMyRoleSection = section.id === 'roles';

  return (
    <div style={{ background: 'white', borderRadius: '14px', border: '1.5px solid #eef0f6', marginBottom: '10px', overflow: 'hidden' }}>
      <button onClick={() => setOpen(v => !v)}
        style={{ width: '100%', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '12px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: '#1e3a6e', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={16} color="white" />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '11px', color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.8px' }}>Section {section.num}</div>
          <div style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{section.title}</div>
        </div>
        {open ? <ChevronUp size={16} color="#94a3b8" /> : <ChevronDown size={16} color="#94a3b8" />}
      </button>

      {open && (
        <div style={{ padding: '0 16px 16px' }}>
          <div style={{ height: '1px', background: '#eef0f6', marginBottom: '14px' }} />

          {/* Section rôles — affichage adapté */}
          {isMyRoleSection ? (
            <div>
              {section.subsections.map((sub, i) => {
                const isMine = role === 'admin' || sub.role === role;
                return (
                  <div key={i} style={{
                    marginBottom: '14px',
                    borderRadius: '12px',
                    border: isMine ? '2px solid #1e3a6e' : '1.5px solid #eef0f6',
                    background: isMine ? '#f0f3f8' : '#fafafa',
                    padding: '14px',
                    opacity: isMine ? 1 : 0.55,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
                      <div style={{
                        display: 'inline-block', fontSize: '11px', fontWeight: 800, color: 'white',
                        background: '#1e3a6e', padding: '3px 10px', borderRadius: '6px',
                        textTransform: 'uppercase', letterSpacing: '0.8px'
                      }}>
                        {sub.title}
                      </div>
                      {isMine && role !== 'admin' && (
                        <div style={{ fontSize: '11px', fontWeight: 700, color: '#1e3a6e', background: 'white', border: '1px solid #c7d4eb', padding: '2px 8px', borderRadius: '6px' }}>
                          Votre rôle
                        </div>
                      )}
                    </div>
                    <p style={{ fontSize: '13px', color: '#374151', lineHeight: 1.7, marginBottom: '10px' }}>{sub.intro}</p>
                    <ul style={{ paddingLeft: '16px', marginBottom: '10px' }}>
                      {sub.items.map((item, j) => (
                        <li key={j} style={{ fontSize: '13px', color: '#374151', lineHeight: 1.7, marginBottom: '4px' }}>{item}</li>
                      ))}
                    </ul>
                    {sub.note && (
                      <div style={{
                        background: '#fdf2f2',
                        borderLeft: '4px solid #b83228',
                        borderRadius: '8px', padding: '10px 13px', fontSize: '12px',
                        color: '#374151',
                        lineHeight: 1.6, fontWeight: 600
                      }}>
                        {sub.note}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            section.content?.map((block, i) => <ContentBlock key={i} block={block} />)
          )}
        </div>
      )}
    </div>
  );
}

// ── Composant principal ────────────────────────────────────────────────────

export default function Reglement({ role = 'admin', onClose }) {
  const isPage = !onClose; // Admin utilise ça comme page, pas panel

  const roleLabel = {
    admin: 'Administrateur',
    driver: 'Livreur',
    gestionnaire: 'Gestionnaire',
  }[role] || 'Équipe';

  const content = (
    <div style={{ background: '#f0f3f8', minHeight: '100%' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg,#07162d 0%,#173b78 100%)',
        padding: '16px 20px 24px',
        position: 'sticky', top: 0, zIndex: 1,
        ...(onClose ? {} : { borderRadius: '0' })
      }}>
        <BookOpen size={80} style={{ position: 'absolute', right: '-10px', top: '-10px', color: 'rgba(255,255,255,0.05)' }} />
        {onClose && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
            <button onClick={onClose}
              style={{ background: 'rgba(255,255,255,0.1)', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.8)', padding: '8px', borderRadius: '10px', display: 'flex' }}>
              <X size={16} />
            </button>
          </div>
        )}
        <div style={{ fontFamily: 'Alan Sans, sans-serif', fontSize: '20px', fontWeight: 800, color: 'white', marginBottom: '4px' }}>
          Règlement Intérieur
        </div>
        <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.5)' }}>
          Wakaana Express · Applicable dès le 05 juin 2026 · {roleLabel}
        </div>
      </div>

      <div style={{ padding: '16px' }}>
        {/* Mot du responsable */}
        <div style={{ background: 'white', borderRadius: '14px', border: '1.5px solid #eef0f6', padding: '16px', marginBottom: '14px' }}>
          <div style={{ fontSize: '11px', fontWeight: 800, color: '#173b78', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px' }}>Mot du Responsable</div>
          <p style={{ fontSize: '13px', color: '#374151', lineHeight: 1.7, marginBottom: '8px' }}>
            Chers membres de l'équipe, depuis le lancement de Wakaana Express, nous avons avancé ensemble et je suis fier du chemin parcouru. Vous faites partie d'une aventure qui grandit, et votre rôle dans cette croissance est essentiel.
          </p>
          <p style={{ fontSize: '13px', color: '#374151', lineHeight: 1.7, marginBottom: '8px' }}>
            Ce document n'est pas une liste de punitions. C'est un engagement mutuel : je m'engage à vous donner les moyens de bien travailler, vous vous engagez à donner le meilleur de vous-mêmes chaque jour.
          </p>
          <div style={{ background: '#f0f3f8', borderLeft: '4px solid #1e3a6e', borderRadius: '8px', padding: '10px 13px', fontSize: '12px', color: '#1e3a6e', fontWeight: 600 }}>
            À partir du 05 juin 2026, les règles décrites dans cette fiche s'appliquent à tous, sans exception.
          </div>
          <div style={{ marginTop: '12px', fontSize: '13px', fontWeight: 700, color: '#0f172a', fontStyle: 'italic' }}>
            Le Responsable, Wakaana Express
          </div>
        </div>

        {/* Sections */}
        {SECTIONS.filter(s => s.roles.includes(role) || role === 'admin').map(section => (
          <Section
            key={section.id}
            section={section}
            role={role}
            defaultOpen={false}
          />
        ))}

        <div style={{ textAlign: 'center', padding: '16px 0 8px', fontSize: '11px', color: '#94a3b8' }}>
          Document interne — Confidentiel · Wakaana Express · 05 juin 2026
        </div>
      </div>
    </div>
  );

  if (isPage) {
    return <div className="page-enter">{content}</div>;
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60, background: '#f0f3f8', overflowY: 'auto' }}>
      {content}
    </div>
  );
}
